import { type IdleState, updateIdleStateAt } from "../state/idle-state";

import type { Clock } from "./types";

export interface IdlePollerOptions {
    idleTimeoutMs: number;
    onIdle: () => void;
    onActive?: () => void;
    pollIntervalMs?: number;
    clock: Clock;
}

// Poll `state` every `pollIntervalMs` and recompute idle vs active. Fires
// `onIdle` once on the active→idle edge and `onActive` once on the reverse.
export class IdlePoller {
    private started = false;
    private intervalId: ReturnType<typeof setInterval> | undefined;
    private lastSeenIdle = false;
    private readonly pollIntervalMs: number;
    private readonly state: IdleState;
    private readonly opts: IdlePollerOptions;

    public constructor(state: IdleState, opts: IdlePollerOptions) {
        this.state = state;
        this.opts = opts;
        this.pollIntervalMs = opts.pollIntervalMs ?? 5000;
    }

    public start = () => {
        if (this.started) {
            return;
        }
        this.started = true;
        this.lastSeenIdle = this.state.isIdle;

        this.intervalId = setInterval(() => {
            const nowIdle = updateIdleStateAt(
                this.state,
                this.opts.clock(),
                this.opts.idleTimeoutMs
            );
            if (nowIdle && !this.lastSeenIdle) {
                this.lastSeenIdle = true;
                this.opts.onIdle();
            } else if (!nowIdle && this.lastSeenIdle) {
                this.lastSeenIdle = false;
                this.opts.onActive?.();
            }
        }, this.pollIntervalMs);
    };

    public stop = () => {
        if (!this.started) {
            return;
        }
        this.started = false;
        if (this.intervalId !== undefined) {
            clearInterval(this.intervalId);
            this.intervalId = undefined;
        }
    };
}
